
import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  User,
  Phone,
  Mail,
  Calendar,
  Shield,
  CheckCircle,
  Clock,
  Save,
  Edit,
  Loader,
  X,
  ArrowLeft,
} from "lucide-react";
//import adminUserService from "../../../services/api/admin.user.service";
import { adminUserService } from "@/services/api/admin.user.service";
import { useNavigate } from "react-router-dom"; // Add this import

const AdminProfile = () => {
  const navigate = useNavigate(); // Add this hook
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await adminUserService.getAdminProfile(token);

      if (response.data.status === "success") {
        setProfileData(response.data.data.admin);
        setEditableData({ ...response.data.data.admin });
      } else {
        throw new Error(response.data.message || "Failed to fetch profile");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setUpdateLoading(true);
      setUpdateError(null);
      setUpdateSuccess(false);

      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Authentication token not found");

      const updateData = {
        fullName: editableData.fullName,
        email: editableData.email,
      };



      const response = await adminUserService.updateAdminProfile(
        updateData,
        token
      );



      if (response.data.status === "success") {
        const updatedProfileData = {
          ...profileData,
          ...response.data.data.admin,
          fullName: response.data.data.admin.fullName || profileData.fullName,
          email: response.data.data.admin.email || profileData.email,
        };

        setProfileData(updatedProfileData);
        setEditableData({ ...updatedProfileData });
        setUpdateSuccess(true);
        setIsEditing(false);

        setTimeout(() => {
          setUpdateSuccess(false);
        }, 3000);
      } else {
        throw new Error(response.data.message || "Failed to update profile");
      }
    } catch (err) {
      setUpdateError(err.message || "Failed to send update request");
    } finally {
      setUpdateLoading(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditableData({ ...profileData });
    setUpdateError(null);
  };

  const handleBackToDashboard = () => {
    navigate("/admindashboard");
  };

  // Format date to a readable format
  const formatDate = (dateString) => {
    if (!dateString) return "Not available";

    try {
      return format(new Date(dateString), "PPP p"); // e.g., April 17, 2025, 5:35 AM
    } catch (err) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-xl font-semibold text-red-600">
          Error loading profile
        </div>
        <div className="mt-2">{error}</div>
        <button
          onClick={fetchProfile}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl font-semibold">No profile data available</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Back to Dashboard Button - Now positioned above the profile card */}
        <div className="mb-6">
          <button
            onClick={handleBackToDashboard}
            className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-white text-gray-700 border border-gray-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm sm:text-base font-medium">Back to Dashboard</span>
          </button>
        </div>

        {/* Main Profile Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header Section */}
          <div className="bg-blue-600 text-white p-4 sm:p-6">
            <div className="flex flex-col space-y-4 lg:flex-row lg:justify-between lg:items-center lg:space-y-0">
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl font-bold">Admin Profile</h1>
                <p className="text-blue-100 text-sm sm:text-base mt-1">Account information and details</p>
              </div>

              <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                {/* Status Badge */}
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-700 text-xs sm:text-sm font-medium w-fit">
                  <span
                    className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${
                      profileData.status === "active"
                        ? "bg-green-400"
                        : "bg-red-400"
                      }`}
                  ></span>
                  {profileData.status === "active" ? "Active" : "Inactive"}
                </span>

                {/* Edit/Save/Cancel Buttons */}
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-blue-700 text-xs sm:text-sm font-medium hover:bg-blue-800 transition-colors w-fit"
                  >
                    <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                    Edit
                  </button>
                ) : (
                  <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
                    <button
                      onClick={handleSaveChanges}
                      disabled={updateLoading}
                      className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-green-600 text-xs sm:text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 w-full sm:w-auto"
                    >
                      {updateLoading ? (
                        <span>Saving...</span>
                      ) : (
                        <>
                          <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                          Save
                        </>
                      )}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={updateLoading}
                      className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-gray-600 text-xs sm:text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 w-full sm:w-auto"
                    >
                      <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status Messages */}
          {updateSuccess && (
            <div
              className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4"
              role="alert"
            >
              <p className="font-medium">Profile updated successfully!</p>
            </div>
          )}

          {updateError && (
            <div
              className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4"
              role="alert"
            >
              <p className="font-medium">Error updating profile</p>
              <p className="text-sm mt-1">{updateError}</p>
            </div>
          )}

          {/* Profile Content */}
          <div className="p-4 sm:p-6">
            {/* Basic Info */}
            <div className="mb-8">
              <h2 className="text-lg sm:text-xl font-semibold mb-4 border-b pb-2">
                Basic Information
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <User className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="w-full min-w-0">
                    <p className="text-sm text-gray-500">Full Name</p>
                    {isEditing ? (
                      <input
                        type="text"
                        name="fullName"
                        value={editableData.fullName || ""}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="Enter your full name"
                      />
                    ) : (
                      <p className="font-medium text-sm sm:text-base break-words">
                        {profileData.fullName || "Not set"}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="w-full min-w-0">
                    <p className="text-sm text-gray-500">Email Address</p>
                    {isEditing ? (
                      <input
                        type="email"
                        name="email"
                        value={editableData.email || ""}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        placeholder="Enter your email"
                      />
                    ) : (
                      <p className="font-medium text-sm sm:text-base break-all">
                        {profileData.email || "Not set"}
                      </p>
                    )}
                    {profileData.isEmailVerified !== undefined && (
                      <span className="inline-flex items-center text-xs text-green-600 mt-1">
                        <CheckCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                        {profileData.isEmailVerified ? "Verified" : "Not Verified"}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Phone className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="w-full min-w-0">
                    <p className="text-sm text-gray-500">Mobile Number</p>
                    <p className="font-medium text-sm sm:text-base">
                      {profileData.mobileNo || "Not set"}
                    </p>
                    {profileData.isMobileVerified !== undefined && (
                      <span className="inline-flex items-center text-xs text-green-600 mt-1">
                        <CheckCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                        {profileData.isMobileVerified ? "Verified" : "Not Verified"}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="w-full min-w-0">
                    <p className="text-sm text-gray-500">Role</p>
                    <p className="font-medium capitalize text-sm sm:text-base">
                      {profileData.role || "Not set"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div>
              <h2 className="text-lg sm:text-xl font-semibold mb-4 border-b pb-2">
                Account Information
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="w-full min-w-0">
                    <p className="text-sm text-gray-500">Created On</p>
                    <p className="font-medium text-sm sm:text-base">
                      {formatDate(profileData.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="w-full min-w-0">
                    <p className="text-sm text-gray-500">Last Login</p>
                    <p className="font-medium text-sm sm:text-base">
                      {formatDate(profileData.lastLogin)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="w-full min-w-0">
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="font-medium text-sm sm:text-base">
                      {formatDate(profileData.updatedAt)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="w-full min-w-0">
                    <p className="text-sm text-gray-500">User ID</p>
                    <p className="font-medium text-xs sm:text-sm break-all">
                      {profileData._id}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;